import type { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import PaginatedResponse from '@/types/paginatedResponse';
import type RepositoryResponse from '@/types/repositoryResponse';
import {
  TreatabilityMatch,
  TreatabilityMatchBulkUpdatePayload,
  TreatabilityMatchCreatePayload
} from '@/types/treatabilityMatch';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export interface ListTreatabilityMatchesParams {
  [key: string]: string | number | undefined | null;
  page?: number | string;
  ordering?: string;
  program?: string;
  program_family?: string;
  phase_type?: string;
  jurisdiction?: string;
  insurance_provider?: string;
  payment_type?: string;
  insurance_type?: string;
  prior_auth_required?: string;
  active_on__gte?: string;
  active_on__lte?: string;
  active_on__isnull?: string;
  active_on__neq?: string;
  active_until__gte?: string;
  active_until__lte?: string;
  active_until__isnull?: string;
  active_until__neq?: string;
}

export const treatabilityMatchRepository = {
  async listTreatabilityMatches({
    host,
    params
  }: {
    host: string;
    params: ListTreatabilityMatchesParams;
  }): Promise<RepositoryResponse<PaginatedResponse<TreatabilityMatch[]>>> {
    try {
      const response = await ajax.get({
        url: `${host}/api/treatability-matches/`,
        params
      });
      return { data: response.data, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to list treatability matches.');
    }
  },

  async getTreatabilityMatch({
    host,
    id
  }: {
    host: string;
    id: string;
  }): Promise<RepositoryResponse<TreatabilityMatch>> {
    try {
      const response = await ajax.get({ url: `${host}/api/treatability-matches/${id}/` });
      return { data: response.data, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get treatability match.');
    }
  },

  async createTreatabilityMatch({
    host,
    data
  }: {
    host: string;
    data: TreatabilityMatchCreatePayload;
  }): Promise<RepositoryResponse<TreatabilityMatch>> {
    try {
      const response = await ajax.post({ url: `${host}/api/treatability-matches/`, data });
      return { data: response.data as unknown as TreatabilityMatch, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to create treatability match.');
    }
  },

  async updateTreatabilityMatch({
    host,
    id,
    data
  }: {
    host: string;
    id: string;
    data: Partial<TreatabilityMatchCreatePayload>;
  }): Promise<RepositoryResponse<TreatabilityMatch>> {
    try {
      const response = await ajax.patch({
        url: `${host}/api/treatability-matches/${id}/`,
        data
      });
      return { data: response.data as unknown as TreatabilityMatch, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to update treatability match.');
    }
  },

  async deleteTreatabilityMatch({
    host,
    id
  }: {
    host: string;
    id: string;
  }): Promise<RepositoryResponse<null>> {
    try {
      await ajax.delete({ url: `${host}/api/treatability-matches/${id}/` });
      return { data: null, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to delete treatability match.');
    }
  },

  async bulkUpdateTreatabilityMatches({
    host,
    payload
  }: {
    host: string;
    payload: TreatabilityMatchBulkUpdatePayload;
  }): Promise<RepositoryResponse<{ updated: number }>> {
    try {
      const response = await ajax.patch({
        url: `${host}/api/treatability-matches/bulk-update/`,
        data: payload
      });
      return { data: response.data as unknown as { updated: number }, isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(
        error as AxiosError,
        'Failed to bulk update treatability matches.'
      );
    }
  },

  async bulkCreateTreatabilityMatches({
    host,
    matches
  }: {
    host: string;
    matches: TreatabilityMatchCreatePayload[];
  }): Promise<RepositoryResponse<TreatabilityMatch[]>> {
    try {
      const response = await ajax.post({
        url: `${host}/api/treatability-matches/bulk-create/`,
        data: matches
      });
      return { data: response.data as unknown as TreatabilityMatch[], isError: false };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(
        error as AxiosError,
        'Failed to bulk create treatability matches.'
      );
    }
  }
};

const TreatabilityMatchRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async listTreatabilityMatches(
      params: ListTreatabilityMatchesParams = {}
    ): Promise<RepositoryResponse<PaginatedResponse<TreatabilityMatch[]>>> {
      return treatabilityMatchRepository.listTreatabilityMatches({ host, params });
    },

    async getTreatabilityMatch(id: string): Promise<RepositoryResponse<TreatabilityMatch>> {
      return treatabilityMatchRepository.getTreatabilityMatch({ host, id });
    },

    async createTreatabilityMatch(
      data: TreatabilityMatchCreatePayload
    ): Promise<RepositoryResponse<TreatabilityMatch>> {
      return treatabilityMatchRepository.createTreatabilityMatch({ host, data });
    },

    async updateTreatabilityMatch(
      id: string,
      data: Partial<TreatabilityMatchCreatePayload>
    ): Promise<RepositoryResponse<TreatabilityMatch>> {
      return treatabilityMatchRepository.updateTreatabilityMatch({ host, id, data });
    },

    async deleteTreatabilityMatch(id: string): Promise<RepositoryResponse<null>> {
      return treatabilityMatchRepository.deleteTreatabilityMatch({ host, id });
    },

    async bulkUpdateTreatabilityMatches(
      payload: TreatabilityMatchBulkUpdatePayload
    ): Promise<RepositoryResponse<{ updated: number }>> {
      return treatabilityMatchRepository.bulkUpdateTreatabilityMatches({ host, payload });
    },

    async bulkCreateTreatabilityMatches(
      matches: TreatabilityMatchCreatePayload[]
    ): Promise<RepositoryResponse<TreatabilityMatch[]>> {
      return treatabilityMatchRepository.bulkCreateTreatabilityMatches({ host, matches });
    }
  };
};

export default TreatabilityMatchRepository;
